import React, { useEffect, useRef, useState } from 'react'
import { ArrowUp, Mic, Reply, Square, X } from 'lucide-react'
import type { IExecutionReply } from 'components/Comments/Comment'
import { Button } from 'components/ui'
import { Attachment } from 'components/ui/Attachment'
import { AttachmentMenu } from 'components/ui/AttachmentMenu'
import {
  ChatComposer,
  ChatComposerActions,
  ChatComposerActionGroup,
  ChatComposerEditor,
} from 'components/ui/ChatComposer'
import { ChatDictation } from 'components/ui/ChatComposer/Dictation'
import { useChatDictation } from 'components/ui/ChatComposer/useChatDictation'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { IHandleCreateComment } from 'constants/interfaces'
import strings from 'constants/strings'
import {
  ExecutionCommentAttachment,
  executionAttachmentKind,
  useExecutionCommentAttachments,
} from './useExecutionCommentAttachments'
import { useExecutionText } from '../TasksOverview/hooks/useExecutionText'

export interface ExecutionCommentDraft {
  text: string
  attachments: ExecutionCommentAttachment[]
}

interface Props {
  replyTarget?: IExecutionReply | null
  onCancelReply?: () => void
  draft?: ExecutionCommentDraft
  getCurrentDraft?: () => ExecutionCommentDraft | undefined
  disabled: boolean
  resetVersion?: number
  onSent?: () => void
  inlineAttachments?: boolean
  allowExpand?: boolean
  onOverlayOpenChange?: (open: boolean) => void
  onDraftChange: (draft: ExecutionCommentDraft) => void
  onSend: (comment: IHandleCreateComment) => Promise<boolean>
}

/** Uses the agent's shared composer; the execution feature owns file drafts and comment transport. */
export const ExecutionCommentComposer: React.FC<Props> = ({
  draft,
  getCurrentDraft,
  disabled,
  resetVersion = 0,
  onSent,
  inlineAttachments = false,
  allowExpand = true,
  onOverlayOpenChange,
  onDraftChange,
  onSend,
  replyTarget,
  onCancelReply,
}) => {
  const t = useExecutionText()

  const [text, setText] = useState(draft?.text || '')
  const [expanded, setExpanded] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(false)

  const seenReset = useRef(resetVersion)
  const textRef = useRef(text)

  textRef.current = text

  const uploads = useExecutionCommentAttachments(draft?.attachments || [], (attachments) => {
    onDraftChange({ text: textRef.current, attachments })
  })

  const { attachments, clear } = uploads

  const input = useRef<HTMLTextAreaElement>(null)
  const anchor = useRef<HTMLDivElement>(null)
  const sendLock = useRef(false)
  const mounted = useRef(true)

  const updateText = (value: string) => {
    textRef.current = value
    setText(value)
    onDraftChange({ text: value, attachments })
  }

  const dictation = useChatDictation({ text, onChange: updateText, active: !disabled && !sending, input })

  const { speech } = dictation
  const busy = disabled || sending
  const canSend = !busy && !speech.listening && uploads.ready && (!!text.trim() || attachments.length > 0)

  const addFiles = (files: File[]) => {
    if (busy || speech.listening) return
    uploads.add(files)
  }

  const send = async () => {
    if (!canSend || sendLock.current) return
    sendLock.current = true
    setSending(true)
    setError(false)
    const submittedDraft = { text, attachments }
    try {
      const accepted = await onSend({
        comment: text.trim(),
        images: attachments.filter(({ file }) => executionAttachmentKind(file) === 'image'),
        videos: attachments.filter(({ file }) => executionAttachmentKind(file) === 'video'),
        files: attachments.filter(({ file }) => executionAttachmentKind(file) === 'file'),
      })
      if (!accepted) {
        if (mounted.current) setError(true)
        return
      }
      // The session can mount a new composer for this thread while this request is in flight.
      // Only clear the exact draft that was submitted; preserve edits made after returning.
      const currentDraft = getCurrentDraft?.()
      if (
        getCurrentDraft &&
        (currentDraft?.text !== submittedDraft.text ||
          currentDraft.attachments.length !== submittedDraft.attachments.length ||
          currentDraft.attachments.some(
            (item, index) => item.previewUrl !== submittedDraft.attachments[index].previewUrl
          ))
      )
        return
      textRef.current = ''
      if (mounted.current) {
        setText('')
        setExpanded(false)
      }
      clear()
      onDraftChange({ text: '', attachments: [] })
      onSent?.()
    } catch {
      if (mounted.current) setError(true)
    } finally {
      if (mounted.current) setSending(false)
      sendLock.current = false
    }
  }

  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  useEffect(() => {
    // A previous instance can finish sending after the user visits another photo and returns.
    if (seenReset.current === resetVersion) return
    seenReset.current = resetVersion
    textRef.current = ''
    setText('')
    clear()
    setError(false)
    setExpanded(false)
  }, [resetVersion, clear])

  useEffect(() => {
    if (replyTarget && !speech.listening) input.current?.focus({ preventScroll: true })
  }, [replyTarget, speech.listening])

  return (
    <div
      ref={anchor}
      className="execution-composer-anchor"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && (event.target as HTMLElement).closest('[role="menu"]')) {
          event.preventDefault()
          event.stopPropagation()
        }
        if (event.key === 'Escape' && speech.listening) {
          event.preventDefault()
          event.stopPropagation()
          dictation.cancel()
        }
      }}
    >
      <DropzoneWrapper
        disabled={busy || speech.listening}
        onDrop={addFiles}
        onPaste={(event) => {
          if (event.clipboardData.files.length) {
            event.preventDefault()
            addFiles(Array.from(event.clipboardData.files))
          }
        }}
      >
        <ChatComposer className={`execution-composer${replyTarget ? ' is-replying' : ''}`}>
          {replyTarget && (
            <div className="execution-reply-target">
              <span className="execution-reply-icon" aria-hidden="true">
                <Reply size={19} />
              </span>
              <div className="execution-reply-context" role="status">
                <div>
                  {t.replyTo} <strong>{replyTarget.name}</strong>
                </div>
                <span>{t.replyHint}</span>
              </div>
              <Button
                variant="icon"
                className="execution-cancel-reply"
                aria-label={t.cancelReply}
                title={t.cancelReply}
                isDisabled={busy}
                onClick={() => {
                  onCancelReply?.()
                  input.current?.focus({ preventScroll: true })
                }}
              >
                <X size={17} />
              </Button>
            </div>
          )}
          {!sending && !!attachments.length && (
            <div className="execution-composer-attachments">
              {attachments.map((item) => (
                <div key={item.previewUrl} className="execution-composer-attachment">
                  <Attachment
                    attachment={{
                      name: item.file.name,
                      mime: item.file.type,
                      size: item.file.size,
                      url: item.previewUrl,
                    }}
                    uploading={!!item.upload}
                    error={item.error}
                    removeDisabled={busy}
                    onRemove={() => uploads.remove(item)}
                  />
                  {item.error && (
                    <Button
                      variant="ghost"
                      className="execution-link"
                      isDisabled={busy}
                      onClick={() => uploads.retry(item)}
                    >
                      {t.retry}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          <ChatDictation {...dictation} />
          <ChatComposerEditor
            id="execution-comment-editor"
            label={replyTarget ? `${t.replyTo} ${replyTarget.name}` : t.writeComment}
            placeholder={replyTarget ? t.writeReply : t.writeComment}
            input={input}
            value={text}
            expanded={allowExpand && expanded}
            hidden={speech.listening}
            disabled={busy}
            onChange={(value) => {
              speech.handleManualInput()
              updateText(value)
            }}
            onSend={send}
            onCancel={dictation.cancel}
            onToggleExpanded={allowExpand ? () => setExpanded(!expanded) : undefined}
          />
          <ChatComposerActions>
            <ChatComposerActionGroup>
              <AttachmentMenu
                triggerButtonProps={{ className: 'execution-composer-action' }}
                usePortal={!inlineAttachments}
                onCameraOpenChange={onOverlayOpenChange}
                anchor={anchor}
                disabled={busy || speech.listening}
                onUpload={(file) => addFiles([file])}
                onUploadFiles={addFiles}
                photoAccept="image/*"
                fileAccept={`${strings.POST_FILE_ACCEPT_EXTENSIONS},.csv,video/*`}
                fileLabel={t.uploadFiles}
                fileHint={t.documentsAndVideos}
              />
            </ChatComposerActionGroup>
            <ChatComposerActionGroup>
              {speech.listening ? (
                <>
                  <Button variant="ghost" className="execution-composer-action" onClick={dictation.cancel}>
                    {t.cancel}
                  </Button>
                  <Button
                    bg="#1e1e1e"
                    onClick={speech.stopListening}
                    startIcon={<Square size={12} fill="currentColor" />}
                  >
                    {t.stopAndInsert}
                  </Button>
                </>
              ) : (
                <>
                  {speech.speechSupported && (
                    <Button
                      variant="icon"
                      className="execution-composer-action"
                      aria-label={t.voiceInput}
                      title={t.voiceInput}
                      isDisabled={busy || speech.isMicrophoneAvailable === false}
                      onClick={dictation.start}
                    >
                      <Mic size={18} />
                    </Button>
                  )}
                  <Button
                    aria-label={replyTarget ? t.sendReply : t.sendComment}
                    title={replyTarget ? t.sendReply : t.sendComment}
                    bg="#1e1e1e"
                    borderRadius="50%"
                    minW="34px"
                    w="34px"
                    h="34px"
                    p={0}
                    isDisabled={!canSend}
                    isLoading={sending}
                    onClick={send}
                  >
                    <ArrowUp size={18} />
                  </Button>
                </>
              )}
            </ChatComposerActionGroup>
          </ChatComposerActions>
        </ChatComposer>
      </DropzoneWrapper>
      {error && (
        <p className="execution-error-text" role="alert">
          {t.commentFailed}
        </p>
      )}
      {speech.isMicrophoneAvailable === false && (
        <p className="execution-error-text" role="alert">
          {t.microphoneDenied}
        </p>
      )}
    </div>
  )
}
